/*
 * LICENSE PROTECTED SOFTWARE
 *
 * Bu yazilim lisans korumasi altindadir.
 * Gecerli lisans olmadan kullanilmasi yasaktir.
 *
 * Kaynak kodunun izinsiz erisimi, kopyalanmasi veya dagitimi
 * hukuki yaptirimslara tabidir.
 * (c) 2026  @cemdemirci0101
 *  bilgi@cemdemirci.com
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution
 * of this file, via any medium, is strictly prohibited.
 * (c) 2026 All Rights Reserved.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line,
} from 'recharts';
import {
  ArrowLeft, Download, Users, BarChart3, Percent,
  Loader2, Star, FileSpreadsheet, Printer,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#ec4899', '#6366f1', '#14b8a6'];

const DistributionChart = ({ distribution, questionType }) => {
  const data = Object.entries(distribution || {}).map(([key, val]) => ({ name: key, value: val }));
  if (!data.length) return <p className="text-sm text-slate-400">Henuz veri yok</p>;
  const total = data.reduce((s, d) => s + d.value, 0);

  if (data.length <= 5 && questionType !== 'rating') {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}>
                {data.map((entry, i) => <Cell key={`cell-${entry.name}-${i}`} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="space-y-2">
          {data.map((d, i) => (
            <div key={`legend-${d.name}-${i}`} className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
              <span className="text-sm flex-1">{d.name}</span>
              <span className="text-sm font-semibold">{d.value}</span>
              <span className="text-xs text-slate-400">({total > 0 ? ((d.value / total) * 100).toFixed(1) : 0}%)</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <ResponsiveContainer width="100%" height={250}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="name" tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip />
          <Bar dataKey="value" fill="#3b82f6" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
      <div className="mt-3 space-y-1">
        {data.map((d, i) => (
          <div key={`bar-${d.name}-${i}`} className="flex items-center justify-between text-sm">
            <span className="text-slate-600">{d.name}</span>
            <span className="font-medium">{d.value} ({total > 0 ? ((d.value / total) * 100).toFixed(1) : 0}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const DailyChart = ({ data }) => {
  const chartData = Object.entries(data || {}).sort().map(([date, count]) => ({ date, count }));
  if (!chartData.length) return null;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <h3 className="text-sm font-semibold text-slate-700 mb-4">Gunluk Katilim</h3>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip />
          <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6', r: 4 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default function SurveyResultsPage() {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  usePageTitle('Anket Sonuclari');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/surveys/${surveyId}/results`, { withCredentials: true });
        setResults(res.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Sonuclar yuklenemedi');
        navigate('/panel/anketler');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [surveyId, navigate]);

  const handleExportExcel = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/surveys/${surveyId}/export`, { withCredentials: true });
      const { survey, questions, responses } = res.data;
      const isAnonymous = !!survey.is_anonymous;
      // Build Excel data
      const headers = isAnonymous
        ? ['Tarih', 'İlçe', 'Mahalle', ...questions.map(q => q.question_text)]
        : ['Kullanici', 'E-posta', 'Telefon', 'Rol', 'İlçe', 'Mahalle', 'Açık Adres', 'Tarih', ...questions.map(q => q.question_text)];
      const rows = responses.map(r => {
        const base = isAnonymous
          ? [
              r.submitted_at ? new Date(r.submitted_at).toLocaleString('tr-TR') : '-',
              r.user_ilce_name || '-',
              r.user_mahalle_name || '-',
            ]
          : [
              r.user_name || 'Anonim',
              r.user_email || '-',
              r.user_phone || '-',
              r.user_role || '-',
              r.user_ilce_name || '-',
              r.user_mahalle_name || '-',
              r.user_acik_adres || '-',
              r.submitted_at ? new Date(r.submitted_at).toLocaleString('tr-TR') : '-',
            ];
        return [
          ...base,
          ...questions.map(q => {
            const ans = r.answers?.[q.id];
            if (Array.isArray(ans)) return ans.join(', ');
            return ans ?? '-';
          }),
        ];
      });
      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Anket Sonuclari');
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([wbout], { type: 'application/octet-stream' }), `anket_${survey.title.replace(/\s/g, '_')}.xlsx`);
      toast.success('Excel dosyasi indirildi');
    } catch {
      toast.error('Export basarisiz');
    }
  };

  const handlePrint = () => window.print();

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  if (!results) return null;

  const { survey, total_responses, question_results, demographics, daily_participation } = results;

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="survey-results-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/panel/anketler')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{survey.title}</h1>
            <p className="text-sm text-slate-500">Anket Sonuclari ve Analiz</p>
          </div>
        </div>
        {hasPermission('surveys.export') && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleExportExcel} data-testid="export-excel-btn">
              <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-btn">
              <Printer className="w-4 h-4 mr-1" /> Yazdir
            </Button>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
          <Users className="w-5 h-5 text-blue-600 mx-auto mb-1" />
          <p className="text-2xl font-bold text-slate-900">{total_responses}</p>
          <p className="text-xs text-slate-500">Toplam Katilim</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
          <BarChart3 className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
          <p className="text-2xl font-bold text-slate-900">{question_results.length}</p>
          <p className="text-xs text-slate-500">Soru Sayisi</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
          <Percent className="w-5 h-5 text-amber-600 mx-auto mb-1" />
          <p className="text-2xl font-bold text-slate-900">
            {total_responses > 0 && question_results.length > 0
              ? ((question_results.reduce((s, q) => s + q.total_answers, 0) / (total_responses * question_results.length)) * 100).toFixed(0)
              : 0}%
          </p>
          <p className="text-xs text-slate-500">Tamamlanma</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
          <Star className="w-5 h-5 text-violet-600 mx-auto mb-1" />
          <p className="text-2xl font-bold text-slate-900">
            {Object.keys(demographics?.by_role || {}).length}
          </p>
          <p className="text-xs text-slate-500">Farkli Rol</p>
        </div>
      </div>

      {/* Daily Participation */}
      <DailyChart data={daily_participation} />

      {/* Question Results */}
      <div className="space-y-6">
        <h2 className="text-lg font-semibold text-slate-800">Soru Bazli Sonuclar</h2>
        {question_results.map((qr, idx) => (
          <div key={qr.question_id} className="bg-white rounded-xl border border-slate-200 p-6" data-testid={`result-q-${idx}`}>
            <div className="flex items-start gap-3 mb-4">
              <span className="text-lg font-bold text-blue-600">{idx + 1}</span>
              <div className="flex-1">
                <h3 className="font-medium text-slate-900">{qr.question_text}</h3>
                <div className="flex gap-3 mt-1">
                  <Badge variant="outline" className="text-xs">{qr.question_type}</Badge>
                  <span className="text-xs text-slate-400">{qr.total_answers} cevap</span>
                </div>
              </div>
            </div>

            {/* Distribution charts */}
            {qr.distribution && (
              <DistributionChart distribution={qr.distribution} questionType={qr.question_type} />
            )}

            {/* Average for rating */}
            {qr.average !== undefined && (
              <div className="flex items-center gap-3 mt-4 p-4 bg-amber-50 rounded-lg">
                <Star className="w-6 h-6 text-amber-500 fill-amber-500" />
                <span className="text-lg font-bold text-amber-700">{qr.average}</span>
                <span className="text-sm text-amber-600">/ 5 ortalama puan</span>
              </div>
            )}

            {/* Average rankings */}
            {qr.average_rankings && Object.keys(qr.average_rankings).length > 0 && (
              <div className="mt-4 space-y-2">
                <h4 className="text-sm font-medium text-slate-600">Ortalama Siralama</h4>
                {Object.entries(qr.average_rankings).sort((a, b) => a[1] - b[1]).map(([item, avg], i) => (
                  <div key={`rank-${item}`} className="flex items-center gap-3">
                    <span className="text-sm font-bold text-slate-400 w-6">{i + 1}.</span>
                    <span className="flex-1 text-sm">{item}</span>
                    <span className="text-xs text-slate-500">ort: {avg}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Text answers */}
            {qr.sample_answers && qr.sample_answers.length > 0 && (
              <div className="mt-4 space-y-2">
                <h4 className="text-sm font-medium text-slate-600">Ornek Cevaplar ({qr.total_answers})</h4>
                {qr.sample_answers.map((a, i) => (
                  <p key={`answer-${i}-${a.slice(0,8)}`} className="text-sm text-slate-600 p-2 bg-slate-50 rounded-lg">"{a}"</p>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Demographics */}
      {demographics && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* By Role */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-sm font-semibold text-slate-700 mb-4">Rol Bazli Dagilim</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={Object.entries(demographics.by_role || {}).map(([k, v]) => ({ name: k, value: v }))}
                  dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                >
                  {Object.keys(demographics.by_role || {}).map((role, i) => <Cell key={`role-${role}`} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          {/* By Ilce */}
          {Object.keys(demographics.by_ilce || {}).length > 1 && (
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">İlçe Bazlı Dağılım</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={Object.entries(demographics.by_ilce || {}).map(([k, v]) => ({ name: k, value: v }))} margin={{ top: 8, right: 8, left: 0, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" interval={0} />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
